import React, { useEffect, useState } from "react";

import { FileText, Loader2, Printer, Truck } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useToast } from "~/hooks/use-toast";
import { useTransportistas } from "~/hooks/useTransportistas";
import { supabase } from "~/integrations/supabase/client";
import { fetchSalesRequestDocuments } from "~/services/salesRequestDocumentService";
import { assignTransportista } from "~/services/transportistaService";
import { SalesRequestDocument } from "~/types/client";

interface BatchTransportistaAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedOrderIds: string[];
  onSuccess: () => void;
}

interface GeneratedFactura {
  salesRequestId: string;
  pdfUrl: string;
  facturaNumber: string;
  relatedDocuments?: SalesRequestDocument[];
}

export const BatchTransportistaAssignmentDialog: React.FC<
  BatchTransportistaAssignmentDialogProps
> = ({ open, onOpenChange, selectedOrderIds, onSuccess }) => {
  const [selectedTransportistaId, setSelectedTransportistaId] =
    useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedFacturas, setGeneratedFacturas] = useState<
    GeneratedFactura[]
  >([]);
  const [isGeneratingPrint, setIsGeneratingPrint] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({
    current: 0,
    total: 0,
  });
  const { toast } = useToast();
  const { transportistas, isLoading } = useTransportistas();

  // Calculate total documents count for display (facturas + related documents excluding facturas)
  const totalDocumentsCount = generatedFacturas.reduce((total, factura) => {
    const relatedDocsCount = factura.relatedDocuments
      ? factura.relatedDocuments.length
      : 0;
    return total + 1 + relatedDocsCount; // 1 for factura + related documents (facturas already filtered out)
  }, 0);

  const generateFactura = async (
    salesRequestId: string
  ): Promise<GeneratedFactura | null> => {
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-factura",
        {
          body: { salesRequestId },
        }
      );

      if (error) {
        // functions.invoke surfaces a non-2xx (incl. our 409 stock block) as a
        // FunctionsHttpError whose body — with the INSUFFICIENT_STOCK message —
        // lives on error.context. Unwrap it so the per-order error is meaningful.
        let message = error.message;
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === "function") {
          const body = await ctx.json().catch(() => null);
          if (body?.error) message = body.error;
        }
        console.error("Error generating factura:", error);
        throw new Error(message);
      }

      // After successful generation, fetch the factura details from database
      const { data: facturaData, error: fetchError } = await supabase
        .from("sales_request_documents")
        .select("url_pdf, bsale_number")
        .eq("sales_request_id", salesRequestId)
        .eq("document_type", "factura")
        .single();

      if (fetchError || !facturaData) {
        console.error("Error fetching factura data:", fetchError);
        return null;
      }

      // Fetch related documents
      const relatedDocuments = await fetchSalesRequestDocuments(salesRequestId);
      const relevantDocuments = relatedDocuments.filter((doc) =>
        ["orden_de_compra", "guia_de_despacho"].includes(doc.documentType)
      );

      return {
        salesRequestId,
        pdfUrl: facturaData.url_pdf,
        facturaNumber: facturaData.bsale_number.toString(),
        relatedDocuments: relevantDocuments,
      };
    } catch (error) {
      console.error("Failed to generate factura:", error);
      throw error;
    }
  };

  const updateOrderStatus = async (salesRequestId: string) => {
    try {
      console.log(
        `📋 Updating status to in_transit for sales request ${salesRequestId}`
      );
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "in_transit",
          updated_at: new Date().toISOString(),
        })
        .eq("id", salesRequestId);

      if (error) {
        console.error("❌ Error updating order status:", error);
        throw error;
      }
      console.log(
        `✅ Successfully updated status to in_transit for sales request ${salesRequestId}`
      );
    } catch (error) {
      console.error("❌ Failed to update order status:", error);
      throw error;
    }
  };

  const downloadPDF = async (
    url: string,
    filename: string,
    onComplete?: () => void
  ) => {
    try {
      console.log(`Starting download: ${filename} from ${url}`);
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);

      const blob = await response.blob();
      console.log(`Downloaded blob size: ${blob.size} bytes for ${filename}`);

      if (blob.size === 0) {
        throw new Error("Downloaded file is empty");
      }

      const downloadUrl = URL.createObjectURL(blob);

      // Create link element and trigger download without appending to DOM
      // This avoids conflicts with React's DOM reconciliation
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.click();

      // Clean up blob URL after delay
      setTimeout(() => {
        URL.revokeObjectURL(downloadUrl);
      }, 1000);

      console.log(`Successfully triggered download: ${filename}`);
      onComplete?.();
    } catch (error) {
      console.error(`Error downloading ${filename}:`, error);
      // Fallback: open in new tab
      window.open(url, "_blank");
      onComplete?.();
    }
  };

  const handlePrintFacturas = async () => {
    if (generatedFacturas.length === 0) {
      toast({
        title: "Error",
        description: "No hay facturas generadas para imprimir",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingPrint(true);
    setDownloadProgress({ current: 0, total: totalDocumentsCount });

    try {
      let downloadIndex = 0;
      let completedDownloads = 0;

      const updateProgress = () => {
        completedDownloads++;
        setDownloadProgress({
          current: completedDownloads,
          total: totalDocumentsCount,
        });

        // If all downloads are complete, reset the loader
        if (completedDownloads >= totalDocumentsCount) {
          setTimeout(() => {
            setIsGeneratingPrint(false);
            setDownloadProgress({ current: 0, total: 0 });
          }, 1000); // Wait 1 second before hiding the loader
        }
      };

      // Download each factura and its related documents
      for (const factura of generatedFacturas) {
        // Download the factura PDF
        const facturaFilename = `factura-${factura.facturaNumber}.pdf`;
        setTimeout(() => {
          downloadPDF(factura.pdfUrl, facturaFilename, updateProgress);
        }, downloadIndex * 800); // Increased delay to 800ms
        downloadIndex++;

        // Download related documents if they exist
        if (factura.relatedDocuments) {
          for (const doc of factura.relatedDocuments) {
            const docTypeNames = {
              orden_de_compra: "orden-compra",
              guia_despacho: "guia-despacho",
              guia_de_despacho_electronica: "guia-despacho-electronica",
              proof_of_delivery: "prueba-entrega",
            };
            const docFileName = `${docTypeNames[doc.documentType] || doc.documentType}-${factura.facturaNumber}.pdf`;
            setTimeout(() => {
              downloadPDF(doc.fileUrl, docFileName, updateProgress);
            }, downloadIndex * 800); // Increased delay to 800ms
            downloadIndex++;
          }
        }
      }

      toast({
        title: "Descargando Documentos",
        description: `Se están descargando ${totalDocumentsCount} documentos (facturas y documentos relacionados). Verifique la carpeta de descargas.`,
        variant: "default",
      });
    } catch (error) {
      console.error("Error downloading PDFs:", error);
      toast({
        title: "Error",
        description: "Error al descargar las facturas",
        variant: "destructive",
      });
      setIsGeneratingPrint(false);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  const handleProcessOrders = async () => {
    if (!selectedTransportistaId) {
      toast({
        title: "Error",
        description: "Debe seleccionar un transportista",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    setGeneratedFacturas([]); // Reset previous facturas

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];
      const newGeneratedFacturas: GeneratedFactura[] = [];

      // Process each order sequentially to avoid overwhelming the system
      for (const orderId of selectedOrderIds) {
        try {
          // 1. Assign transportista
          console.log(
            `Step 1: Assigning transportista ${selectedTransportistaId} to order ${orderId}`
          );
          await assignTransportista(orderId, selectedTransportistaId);

          // 2. Generate factura and collect details
          console.log(`Step 2: Generating factura for order ${orderId}`);
          const facturaDetails = await generateFactura(orderId);
          if (facturaDetails) {
            newGeneratedFacturas.push(facturaDetails);
          }

          // 3. Change status to in_transit
          console.log(
            `Step 3: Updating status to in_transit for order ${orderId}`
          );
          await updateOrderStatus(orderId);

          console.log(`✅ Successfully processed order ${orderId}`);
          successCount++;
        } catch (error) {
          console.error(`Error processing order ${orderId}:`, error);
          errorCount++;
          errors.push(
            `Error procesando orden ${orderId}: ${error.message || "Error desconocido"}`
          );
        }
      }

      // Store generated facturas for printing
      setGeneratedFacturas(newGeneratedFacturas);

      // Show results
      if (successCount > 0 && errorCount === 0) {
        toast({
          title: "Procesamiento Exitoso",
          description: `Se procesaron exitosamente ${successCount} notas de venta. Se asignó el transportista, se generaron las facturas y se cambiaron los estados a "En Tránsito". Ahora puede imprimir las facturas.`,
          variant: "default",
        });
      } else if (successCount > 0 && errorCount > 0) {
        toast({
          title: "Procesamiento Parcial",
          description: `Se procesaron ${successCount} notas exitosamente. ${errorCount} fallaron. Puede imprimir las facturas generadas exitosamente.`,
          variant: "default",
        });
        console.error("Errors during batch processing:", errors);
      } else {
        toast({
          title: "Error en Procesamiento",
          description: `No se pudo procesar ninguna nota de venta. Revise los detalles en la consola.`,
          variant: "destructive",
        });
        console.error("All orders failed:", errors);
      }

      // Refresh data but keep dialog open for printing
      onSuccess();
    } catch (error) {
      console.error("Batch processing error:", error);
      toast({
        title: "Error",
        description: "Error durante el procesamiento por lotes",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setSelectedTransportistaId("");
    setGeneratedFacturas([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Asignar Transportista y Procesar
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {generatedFacturas.length === 0 ? (
            <div className="rounded-lg bg-blue-50 p-4">
              <p className="text-sm text-blue-800">
                <strong>
                  Se procesarán {selectedOrderIds.length} notas de venta:
                </strong>
              </p>
              <ul className="mt-2 space-y-1 text-sm text-blue-700">
                <li>• Se asignará el transportista seleccionado</li>
                <li>• Se generarán las facturas automáticamente</li>
                <li>• Se cambiará el estado a "En Tránsito"</li>
              </ul>
            </div>
          ) : (
            <div className="rounded-lg bg-green-50 p-4">
              <p className="text-sm text-green-800">
                <strong>
                  ✅ Se generaron {generatedFacturas.length} facturas
                  exitosamente:
                </strong>
              </p>
              <ul className="mt-2 space-y-2 text-sm text-green-700">
                {generatedFacturas.map((factura) => (
                  <li
                    key={factura.salesRequestId}
                    className="border-l-2 border-green-300 pl-3"
                  >
                    <div className="font-medium">
                      • Factura #{factura.facturaNumber}
                    </div>
                    {factura.relatedDocuments &&
                      factura.relatedDocuments.length > 0 && (
                        <div className="ml-4 mt-1 text-xs text-green-600">
                          <span className="font-medium">
                            Documentos relacionados:
                          </span>
                          <ul className="mt-1 space-y-1">
                            {factura.relatedDocuments.map((doc, index) => {
                              const displayName =
                                doc.documentType === "orden_de_compra"
                                  ? "Orden de Compra"
                                  : doc.documentType === "guia_despacho"
                                    ? "Guía de Despacho"
                                    : doc.documentType ===
                                        "guia_de_despacho_electronica"
                                      ? "Guía de Despacho Electrónica"
                                      : doc.documentType === "proof_of_delivery"
                                        ? "Prueba de Entrega"
                                        : doc.documentType === "factura"
                                          ? "Factura"
                                          : doc.documentType;
                              console.log(
                                `Document type: ${doc.documentType}, Display name: ${displayName}`
                              );
                              return <li key={index}>- {displayName}</li>;
                            })}
                          </ul>
                        </div>
                      )}
                  </li>
                ))}
              </ul>
              <div className="mt-3 border-t border-green-200 pt-3">
                <p className="text-sm text-green-800">
                  📄 Puede descargar todas las facturas y documentos
                  relacionados (total: {totalDocumentsCount} documentos)
                </p>

                {/* Download Progress Bar */}
                {isGeneratingPrint && downloadProgress.total > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="flex justify-between text-xs text-green-700">
                      <span>Descargando documentos...</span>
                      <span>
                        {downloadProgress.current}/{downloadProgress.total}
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-green-100">
                      <div
                        className="h-2 rounded-full bg-green-600 transition-all duration-300 ease-out"
                        style={{
                          width: `${(downloadProgress.current / downloadProgress.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {generatedFacturas.length === 0 && (
            <div className="space-y-2">
              <Label htmlFor="transportista-select">
                Seleccionar Transportista
              </Label>
              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">
                    Cargando transportistas...
                  </span>
                </div>
              ) : (
                <Select
                  value={selectedTransportistaId}
                  onValueChange={setSelectedTransportistaId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione un transportista" />
                  </SelectTrigger>
                  <SelectContent>
                    {transportistas.map((transportista) => (
                      <SelectItem
                        key={transportista.id}
                        value={transportista.id}
                      >
                        {transportista.name} {transportista.lastname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isProcessing || isGeneratingPrint}
          >
            {generatedFacturas.length > 0 ? "Cerrar" : "Cancelar"}
          </Button>

          {generatedFacturas.length === 0 ? (
            <Button
              onClick={handleProcessOrders}
              disabled={!selectedTransportistaId || isLoading || isProcessing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Procesar Notas
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handlePrintFacturas}
              disabled={isGeneratingPrint}
              className="bg-green-600 hover:bg-green-700"
            >
              {isGeneratingPrint ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {downloadProgress.total > 0
                    ? `Descargando ${downloadProgress.current}/${downloadProgress.total}...`
                    : "Preparando descarga..."}
                </>
              ) : (
                <>
                  <Printer className="mr-2 h-4 w-4" />
                  Descargar Documentos PDF
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
